import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Props {
  leagueId: number;
  squadId: number | null; // null = all squads
  onClose: () => void;
  onAdded: () => void;
}

interface FormData {
  name: string;
  type: string;
  applies_to: 'bowler' | 'doubles';
  applies_to_game_numbers: string;
  cost: number;
  bracket_size: number;
  rounds: number;
  advancement_type: string;
  seeding_method: string;
  games_applied_to: string;
  is_reversed: boolean;
  applies_to_series: boolean;
}

const TYPES = [
  { value: 'high_game', label: 'High Game' },
  { value: 'high_series', label: 'High Series' },
  { value: 'mystery_score', label: 'Mystery Score' },
  { value: 'mystery_series', label: 'Mystery Series' },
  { value: 'side_pots', label: 'Side Pots' },
  { value: 'custom', label: 'All with All' },
  { value: 'survivor', label: 'Survivor' },
  { value: 'eliminator', label: 'Eliminator' },
  { value: 'clean_game', label: 'Clean Game' },
  { value: 'bracket', label: 'Bracket' },
  
];

const BookieAddSideEventModal: React.FC<Props> = ({ leagueId, squadId, onClose, onAdded }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState<FormData>({
    name: '',
    type: 'high_game',
    applies_to: 'bowler',
    applies_to_game_numbers: '',
    cost: 0,
    bracket_size: 8,
    rounds: 3,
    advancement_type: 'higher_score',
    seeding_method: 'random',
    games_applied_to: '',
    is_reversed: false,
    applies_to_series: false,
  });

  const [survivorRounds, setSurvivorRounds] = useState<{ game_number: number; survivor_score: number; round_payout: number }[]>([]);
  const [eliminatorRounds, setEliminatorRounds] = useState<{ game_number: number; advancement_percentage: number; round_payout: number }[]>([]);
  const [sidePots, setSidePots] = useState<{ config_type: string; game_identifier: string; winners_count: number; payout_amount: number }[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Eligibility filter state ──────────────────────────────────────────────
  const [femaleOnly, setFemaleOnly] = useState(false);
  const [seniorsOnly, setSeniorsOnly] = useState(false);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');

  // Bracket payout selects
  const [payoutFirst, setPayoutFirst] = useState(5);
  const [payoutSecond, setPayoutSecond] = useState(2);

  // Derived bracket values (computed on every render)
  const bracketGameNums = form.type === 'bracket'
    ? form.applies_to_game_numbers.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0)
    : [];
  const numBracketGames = bracketGameNums.length;
  const derivedBracketSize = numBracketGames >= 2 ? Math.pow(2, numBracketGames) : 0;
  const derivedRounds = numBracketGames;
  const payoutAdmin = derivedBracketSize > 0 ? derivedBracketSize - payoutFirst - payoutSecond : 0;

  // Auto-populate game numbers for series types + reset applies_to for types that don't support doubles
  useEffect(() => {
    if ((form.type === 'mystery_series' || form.type === 'high_series') && !form.applies_to_game_numbers) {
      setForm(prev => ({ ...prev, applies_to_game_numbers: '1,2,3' }));
    }
    if (['clean_game', 'bracket'].includes(form.type) && form.applies_to === 'doubles') {
      setForm(prev => ({ ...prev, applies_to: 'bowler' }));
    }
    if (form.type === 'custom') {
      setForm(prev => ({ ...prev, applies_to: 'doubles' }));
    }
  }, [form.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate round configs from game numbers
  useEffect(() => {
    if (!form.applies_to_game_numbers) return;
    const games = form.applies_to_game_numbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
    if (form.type === 'survivor') {
      setSurvivorRounds(games.map(g => ({ game_number: g, survivor_score: 150, round_payout: 0 })));
    } else if (form.type === 'eliminator') {
      setEliminatorRounds(games.map(g => ({ game_number: g, advancement_percentage: 50, round_payout: 0 })));
    } else if (form.type === 'side_pots' || form.type === 'custom') {
      setSidePots(games.map(g => ({ config_type: 'individual_game', game_identifier: String(g), winners_count: 10, payout_amount: 0 })));
    }
  }, [form.applies_to_game_numbers, form.type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
               type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t('bookie.addSE.nameRequired', 'Name is required')); return; }
    if (!form.applies_to_game_numbers.trim() && !['mystery_series', 'high_series'].includes(form.type)) {
      setError(t('bookie.addSE.gameNumRequired', 'Game numbers are required (e.g. 1,2,3)')); return;
    }
    // Bracket-specific validation
    if (form.type === 'bracket') {
      if (numBracketGames < 2) { setError('Enter at least 2 game numbers to define a bracket (2 games = 4-man, 3 = 8-man, 4 = 16-man)'); return; }
      if (payoutFirst < 1) { setError('1st place payout must be at least 1 entry'); return; }
      if (payoutSecond < 1) { setError('2nd place payout must be at least 1 entry'); return; }
      if (payoutAdmin < 0) { setError(`Payout entries exceed bracket size (${derivedBracketSize}). Reduce 1st or 2nd place.`); return; }
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: form.name,
        type: form.type,
        applies_to: form.applies_to,
        applies_to_game_numbers: form.applies_to_game_numbers || '1,2,3',
        cost: form.cost,
        payout_formula: form.type === 'bracket' ? `${payoutFirst},${payoutSecond},${payoutAdmin}` : '',
        applies_to_series: form.applies_to_series,
        bracket_size: form.type === 'bracket' ? derivedBracketSize : form.bracket_size,
        rounds: form.type === 'bracket' ? derivedRounds : form.rounds,
        advancement_type: form.type === 'bracket' ? 'higher_score' : form.advancement_type,
        seeding_method: form.type === 'bracket' ? 'random' : form.seeding_method,
        games_applied_to: form.type === 'bracket'
          ? (form.applies_to_game_numbers || '1,2,3')
          : (form.games_applied_to || form.applies_to_game_numbers || '1,2,3'),
        is_reversed: form.is_reversed,
        survivor_rounds: survivorRounds,
        eliminator_rounds: eliminatorRounds,
        side_pots: sidePots,
        gender: femaleOnly ? 'F' : null,
        min_age: seniorsOnly ? 50 : (minAge ? parseInt(minAge) : null),
        max_age: maxAge ? parseInt(maxAge) : null,
      };

      const url = squadId
        ? `${API}/bookie-league-management/squads/${squadId}/side-events`
        : `${API}/bookie-league-management/leagues/${leagueId}/side-events`;

      await axios.post(url, payload, { headers: { Authorization: `Bearer ${token}` } });
      onAdded();
    } catch (err: any) {
      setError(err.response?.data?.error || t('bookie.addSE.saveFailed', 'Failed to save side event'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500';
  const labelCls = 'block text-sm font-medium text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">
              {squadId
                ? t('bookie.addSE.titleSquad', 'Add Side Event (This Squad)')
                : t('bookie.addSE.titleAll', 'Add Side Event (All Squads)')}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {squadId
                ? t('bookie.addSE.subtitleSquad', 'Creates for the selected squad only')
                : t('bookie.addSE.subtitleAll', 'Creates for every squad in this league')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Row 1: Name + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('bookie.addSE.name', 'Name')} *</label>
              <input name="name" value={form.name} onChange={handleChange} className={inputCls} placeholder="e.g. High Game Pot" />
            </div>
            <div>
              <label className={labelCls}>{t('bookie.addSE.type', 'Type')} *</label>
              <select name="type" value={form.type} onChange={handleChange} className={inputCls} style={{ colorScheme: 'dark' }}>
                {TYPES.map(t => <option key={t.value} value={t.value} className="bg-gray-800">{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Applies To + Cost */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('bookie.addSE.appliesTo', 'Applies To')} *</label>
              <select name="applies_to" value={form.applies_to} onChange={handleChange} className={inputCls} style={{ colorScheme: 'dark' }}
                disabled={form.type === 'custom'}>
                <option value="bowler" className="bg-gray-800">{t('bookie.addSE.applyBowler', 'Individual Bowler')}</option>
                {!['clean_game', 'bracket'].includes(form.type) && (
                  <option value="doubles" className="bg-gray-800">{t('bookie.addSE.applyDoubles', 'Doubles')}</option>
                )}
              </select>
              {form.type === 'custom' && (
                <p className="text-xs text-yellow-400 mt-1">All with All always applies to doubles.</p>
              )}
            </div>
            <div>
              <label className={labelCls}>{t('bookie.addSE.cost', 'Cost ($)')}</label>
              <input name="cost" type="number" min="0" step="0.50" value={form.cost} onChange={handleChange} className={inputCls} />
            </div>
          </div>

          {/* Game Numbers */}
          <div>
            <label className={labelCls}>
              {t('bookie.addSE.gameNumbers', 'Game Numbers')}
              {!['mystery_series', 'high_series'].includes(form.type) && ' *'}
            </label>
            <input name="applies_to_game_numbers" value={form.applies_to_game_numbers} onChange={handleChange}
              className={inputCls} placeholder="e.g. 1,2,3" />
            <p className="text-xs text-gray-500 mt-1">{t('bookie.addSE.gameNumbersHint', 'Comma-separated game numbers (1-9)')}</p>
          </div>

          {/* Payout Formula — brackets only, handled in bracket config section below */}

          {/* Bracket Config */}
          {form.type === 'bracket' && (
            <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-semibold text-purple-300">{t('bookie.addSE.bracketConfig', 'Bracket Configuration')}</h3>

              {/* Warning if not enough games */}
              {derivedBracketSize === 0 && (
                <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-700/40 rounded-lg px-3 py-2">
                  Enter at least 2 game numbers above to configure the bracket (2&nbsp;games&nbsp;=&nbsp;4-man, 3&nbsp;=&nbsp;8-man, 4&nbsp;=&nbsp;16-man)
                </p>
              )}

              {/* Locked info pills */}
              {derivedBracketSize > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className={labelCls}>{t('bookie.addSE.bracketSize', 'Bracket Size')}</label>
                    <div className="px-3 py-2 bg-gray-600/50 border border-gray-600 rounded-lg text-gray-300 text-sm font-medium">
                      {derivedBracketSize}-man
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t('bookie.addSE.rounds', 'Rounds')}</label>
                    <div className="px-3 py-2 bg-gray-600/50 border border-gray-600 rounded-lg text-gray-300 text-sm font-medium">
                      {derivedRounds}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t('bookie.addSE.advancement', 'Advancement')}</label>
                    <div className="px-3 py-2 bg-gray-600/50 border border-gray-600 rounded-lg text-gray-300 text-sm">
                      Higher Score
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t('bookie.addSE.seeding', 'Seeding')}</label>
                    <div className="px-3 py-2 bg-gray-600/50 border border-gray-600 rounded-lg text-gray-300 text-sm">
                      Random
                    </div>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                      <input type="checkbox" name="is_reversed" checked={form.is_reversed}
                        onChange={handleChange} className="rounded border-gray-600" />
                      {t('bookie.addSE.reversed', 'Reversed')}
                      <span className="text-xs text-gray-500">(last game = Round 1)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Payout Formula Selects */}
              {derivedBracketSize > 0 && (
                <div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-gray-200">Payout Entries</h4>
                    <span className="text-xs text-gray-400">(total must equal {derivedBracketSize} — entries per bracket)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {/* 1st Place */}
                    <div>
                      <label className={labelCls}>🥇 1st Place *</label>
                      <select
                        value={payoutFirst}
                        onChange={e => {
                          const f = +e.target.value;
                          setPayoutFirst(f);
                          // Clamp second so sum stays within bracketSize
                          if (payoutSecond > derivedBracketSize - f)
                            setPayoutSecond(Math.max(1, derivedBracketSize - f));
                        }}
                        className={inputCls}
                        style={{ colorScheme: 'dark' }}
                      >
                        {Array.from({ length: derivedBracketSize - 1 }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n} className="bg-gray-800">{n}</option>
                        ))}
                      </select>
                    </div>

                    {/* 2nd Place */}
                    <div>
                      <label className={labelCls}>🥈 2nd Place *</label>
                      <select
                        value={payoutSecond}
                        onChange={e => setPayoutSecond(+e.target.value)}
                        className={inputCls}
                        style={{ colorScheme: 'dark' }}
                      >
                        {Array.from({ length: Math.max(0, derivedBracketSize - payoutFirst) }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n} className="bg-gray-800">{n}</option>
                        ))}
                      </select>
                    </div>

                    {/* Admin (auto-computed) */}
                    <div>
                      <label className={labelCls}>💼 Admin (Myself)</label>
                      <div className={`px-3 py-2 border rounded-lg text-sm font-medium ${
                        payoutAdmin < 0
                          ? 'bg-red-900/40 border-red-500 text-red-400'
                          : 'bg-gray-600/50 border-gray-600 text-gray-300'
                      }`}>
                        {payoutAdmin < 0 ? 'Too many entries' : `${payoutAdmin} entr${payoutAdmin === 1 ? 'y' : 'ies'}`}
                      </div>
                    </div>
                  </div>

                  {/* Formula preview */}
                  <p className="text-xs text-gray-500 mt-2">
                    Formula stored as: <span className="text-gray-300 font-mono">{payoutFirst},{payoutSecond},{Math.max(0, payoutAdmin)}</span>
                    &ensp;·&ensp; Total: <span className={payoutFirst + payoutSecond + Math.max(0, payoutAdmin) === derivedBracketSize ? 'text-green-400' : 'text-amber-400'}>
                      {payoutFirst + payoutSecond + Math.max(0, payoutAdmin)}/{derivedBracketSize}
                    </span>
                    {payoutFirst + payoutSecond + Math.max(0, payoutAdmin) === derivedBracketSize && ' ✓'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Most common for {derivedBracketSize}-man:
                    {derivedBracketSize === 4 && ' 2,1,1'}
                    {derivedBracketSize === 8 && ' 5,2,1'}
                    {derivedBracketSize === 16 && ' 10,4,2'}
                    {derivedBracketSize === 32 && ' 20,8,4'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Survivor Config */}
          {form.type === 'survivor' && survivorRounds.length > 0 && (
            <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-purple-300 mb-3">{t('bookie.addSE.survivorRounds', 'Survivor Rounds')}</h3>
              <div className="space-y-2">
                {survivorRounds.map((round, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-gray-700 px-3 py-1.5 rounded text-gray-300">Game {round.game_number}</div>
                    <div>
                      <input type="number" placeholder="Min Score" value={round.survivor_score}
                        onChange={e => setSurvivorRounds(prev => prev.map((r, idx) => idx === i ? { ...r, survivor_score: +e.target.value } : r))}
                        className={inputCls + ' text-sm py-1.5'} />
                    </div>
                    <div>
                      <input type="number" placeholder="Payout $" value={round.round_payout}
                        onChange={e => setSurvivorRounds(prev => prev.map((r, idx) => idx === i ? { ...r, round_payout: +e.target.value } : r))}
                        className={inputCls + ' text-sm py-1.5'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Eliminator Config */}
          {form.type === 'eliminator' && eliminatorRounds.length > 0 && (
            <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-purple-300 mb-3">{t('bookie.addSE.eliminatorRounds', 'Eliminator Rounds')}</h3>
              <div className="space-y-2">
                {eliminatorRounds.map((round, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-gray-700 px-3 py-1.5 rounded text-gray-300">Game {round.game_number}</div>
                    <div>
                      <select
                        value={round.advancement_percentage}
                        onChange={e => setEliminatorRounds(prev => prev.map((r, idx) => idx === i ? { ...r, advancement_percentage: +e.target.value } : r))}
                        className={inputCls + ' text-sm py-1.5'}
                        style={{ colorScheme: 'dark' }}
                      >
                        <option value={25} className="bg-gray-800">1 of 4 advance (25%)</option>
                        <option value={33} className="bg-gray-800">1 of 3 advance (33%)</option>
                        <option value={50} className="bg-gray-800">1 of 2 advance (50%)</option>
                        <option value={67} className="bg-gray-800">2 of 3 advance (67%)</option>
                        <option value={75} className="bg-gray-800">3 of 4 advance (75%)</option>
                        <option value={85} className="bg-gray-800">Top 85% advance (85%)</option>
                        <option value={100} className="bg-gray-800">All advance (100%)</option>
                      </select>
                    </div>
                    <div>
                      <input type="number" placeholder="Round Payout $" value={round.round_payout}
                        onChange={e => setEliminatorRounds(prev => prev.map((r, idx) => idx === i ? { ...r, round_payout: +e.target.value } : r))}
                        className={inputCls + ' text-sm py-1.5'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Side Pots Config (also used for All with All) */}
          {(form.type === 'side_pots' || form.type === 'custom') && (
            <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-purple-300">{t('bookie.addSE.sidePots', 'Side Pots')}</h3>
                <button type="button"
                  onClick={() => setSidePots(prev => [...prev, { config_type: 'game_series', game_identifier: form.applies_to_game_numbers || '1,2,3', winners_count: 1, payout_amount: 0 }])}
                  className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg">
                  + Add Series Pot
                </button>
              </div>
              {sidePots.length === 0 && <p className="text-xs text-gray-500">Enter game numbers above to auto-generate individual game pots.</p>}
              {/* Column headers */}
              {sidePots.length > 0 && (
                <div className="grid grid-cols-[1fr_1fr_100px_28px] gap-2 text-[10px] text-gray-500 uppercase tracking-wide mb-1 px-1">
                  <span>Pot</span><span>Top % Winners</span><span>Payout $</span><span/>
                </div>
              )}
              <div className="space-y-2">
                {sidePots.map((pot, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_100px_28px] gap-2 text-sm items-center">
                    {pot.config_type === 'game_series'
                      ? <input value={pot.game_identifier}
                          onChange={e => setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, game_identifier: e.target.value } : p))}
                          placeholder="Games e.g. 1,2,3"
                          className={inputCls + ' text-sm py-1.5'} />
                      : <div className="bg-gray-700 px-3 py-1.5 rounded text-gray-300 text-sm">Game {pot.game_identifier}</div>
                    }
                    <select value={pot.winners_count}
                        onChange={e => setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, winners_count: +e.target.value } : p))}
                        className={inputCls + ' text-sm py-1.5'}>
                        {[1,2,3,4,5,6,7,8,9,10].map(v => (
                          <option key={v} value={v} className="bg-gray-800">{v * 10}%</option>
                        ))}
                      </select>
                    <input type="number" placeholder="0" value={pot.payout_amount}
                      onChange={e => setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, payout_amount: +e.target.value } : p))}
                      className={inputCls + ' text-sm py-1.5'} />
                    <button type="button" onClick={() => setSidePots(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-gray-500 hover:text-red-400 p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Eligibility Filters (Optional) ────────────────────────── */}
          <div className="bg-gray-700/30 border border-gray-600/50 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Eligibility Filters <span className="text-gray-600 font-normal normal-case">(Optional)</span>
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
              <input type="checkbox" checked={femaleOnly} onChange={e => setFemaleOnly(e.target.checked)}
                className="rounded border-gray-600" />
              Female only event?
            </label>
            <label className={`flex items-center gap-2 text-sm ${(minAge || maxAge) ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-gray-300 cursor-pointer'}`}>
              <input type="checkbox" checked={seniorsOnly} disabled={!!(minAge || maxAge)}
                onChange={e => { setSeniorsOnly(e.target.checked); }}
                className="rounded border-gray-600 disabled:cursor-not-allowed" />
              Seniors event?
              <span className="text-xs text-gray-500">(sets min age to 50)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Min Age <span className="text-gray-600">(Optional)</span>
                </label>
                <input type="number" min={1} max={100} value={minAge} placeholder="e.g. 50"
                  onChange={e => { setMinAge(e.target.value); if (e.target.value) setSeniorsOnly(false); }}
                  className={inputCls + ' text-sm'} />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Max Age <span className="text-gray-600">(Optional)</span>
                </label>
                <input type="number" min={1} max={100} value={maxAge} placeholder="e.g. 65"
                  onChange={e => { setMaxAge(e.target.value); if (e.target.value) setSeniorsOnly(false); }}
                  className={inputCls + ' text-sm'} />
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-900/40 border border-red-500 text-red-300 text-sm p-3 rounded-lg">{error}</div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium text-white transition-colors">
              {t('bookie.addSE.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm font-bold text-white transition-colors flex items-center gap-2">
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? t('bookie.addSE.saving', 'Adding...') : t('bookie.addSE.submit', 'Add Side Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookieAddSideEventModal;
